import React from "react";
import { Link } from "react-router-dom";
import { getCardTitle, getPagesByCluster, type SeoCluster } from "@shared/config/seo-pages";
import { ArrowRight } from "lucide-react";

interface RelatedPagesProps {
  /** Путь текущей страницы — она исключается из подборки */
  currentPath: string;
  cluster?: SeoCluster;
  limit?: number;
}

const CLUSTER_TITLES: Record<SeoCluster, string> = {
  format: "Другие форматы файлов",
  language: "Другие языки",
  usecase: "Похожие задачи",
  platform: "Записи с других платформ",
  export: "Другие форматы результата"
};

const CLUSTER_HUBS: Partial<Record<SeoCluster, { path: string; label: string }>> = {
  format: { path: "/formats", label: "Все форматы" },
  language: { path: "/languages", label: "Все языки" },
  usecase: { path: "/use-cases", label: "Все задачи" },
  platform: { path: "/platforms", label: "Все платформы" },
  export: { path: "/exports", label: "Все форматы выгрузки" }
};

/**
 * Блок перелинковки внутри тематической группы.
 * Соседи берутся по кругу от текущей страницы, чтобы ссылки распределялись
 * равномерно, а не сходились на первых элементах списка.
 */
export const RelatedPages: React.FC<RelatedPagesProps> = ({ currentPath, cluster, limit = 4 }) => {
  if (!cluster) {
    return null;
  }

  // URL может прийти с завершающим слешем — пути в конфиге хранятся без него
  const normalizedPath = currentPath.length > 1 ? currentPath.replace(/\/+$/, "") : currentPath;
  const pages = getPagesByCluster(cluster);
  const currentIndex = pages.findIndex(page => page.path === normalizedPath);

  if (currentIndex === -1 || pages.length <= 1) {
    return null;
  }

  const related = Array.from({ length: Math.min(limit, pages.length - 1) }, (_, offset) => {
    return pages[(currentIndex + offset + 1) % pages.length];
  });

  const hub = CLUSTER_HUBS[cluster];

  return (
    <section className="border-t border-border bg-background py-16">
      <div className="container px-4 md:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex items-baseline justify-between gap-4">
            <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">{CLUSTER_TITLES[cluster]}</h2>
            {hub && (
              <Link to={hub.path} className="whitespace-nowrap text-sm font-medium text-primary hover:underline">
                {hub.label}
              </Link>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {related.map(page => (
              <Link
                key={page.path}
                to={page.path}
                className="group rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <div className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                  {getCardTitle(page)}
                  <ArrowRight className="h-4 w-4 shrink-0 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
                <p className="text-sm text-muted-foreground">{page.customSubtitle}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
